import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Search, X, BookOpen, Heart, Sparkles, ChevronRight, Link2, Check } from 'lucide-react';
import { wholeSelfResources } from '../data/resources';
import { wholeSelfContent, SECTION_ICON } from '../data/wholeSelfContent';
import { wellnessContent } from '../data/wellnessContent';
import ArticleSections from '../components/ArticleSections';
import NewsletterSignup from '../components/NewsletterSignup';
import useNewsletterPrompt from '../hooks/useNewsletterPrompt';
import { useResourceDeepLink } from '../hooks/useResourceDeepLink';

// Helper component to render icon - either styled circle or emoji
const SectionIcon = ({ icon, size = 24 }) => {
  if (icon === 'logo') {
    return (
      <span 
        className="inline-block rounded-full bg-terracotta border-2 border-terracotta-dark shadow-sm"
        style={{ 
          width: size * 0.5, 
          height: size * 0.5,
          minWidth: size * 0.5,
          minHeight: size * 0.5
        }}
      />
    );
  }
  return <span className="text-2xl">{icon}</span>;
};

const WholeSelfResourcesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  
  // Newsletter soft prompt
  const { showPrompt, dismissPrompt } = useNewsletterPrompt('whole-self-resources');

  const getDetailedContent = (resourceId) => {
    return wholeSelfContent[resourceId] || wellnessContent[resourceId] || null;
  };

  // Deep linking
  const { setResourceParam, clearResourceParam, copyResourceLink, copied } = useResourceDeepLink({
    resources: wholeSelfResources,
    getDetailedContent,
    onSelect: setSelectedResource,
  });

  const filteredResources = wholeSelfResources.filter(resource =>
    resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getTypeColor = (type) => {
    if (type === 'PDF Guide') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (type === 'Coming Soon') return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-violet-100 text-violet-700 border-violet-200';
  };

  const handleResourceClick = (resource) => {
    const detailed = getDetailedContent(resource.id);
    if (detailed) {
      setSelectedResource({ ...resource, detailed });
      setResourceParam(resource.id);
    } else if (resource.downloadUrl) {
      window.open(resource.downloadUrl, '_blank');
    }
  };

  const handleCloseResource = () => {
    setSelectedResource(null);
    clearResourceParam();
  };

  // Build a clean, printable document (user can Save as PDF from the print dialog).
  const printArticle = (d) => {
    if (!d) return;
    const esc = (s = '') => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const secHtml = (d.sections || []).map((s) => {
      let h = `<h2>${esc(s.title)}</h2>`;
      (s.paragraphs || []).forEach((p) => { h += `<p>${esc(p)}</p>`; });
      if (s.intro) s.intro.split('\n\n').forEach((p) => { h += `<p>${esc(p)}</p>`; });
      (s.groups || []).forEach((g) => {
        h += `<div class="group"><p class="gt">${esc(g.title)}</p><ul>`;
        (g.items || []).forEach((it) => { h += `<li>${esc(it)}</li>`; });
        h += `</ul></div>`;
      });
      if (s.listItems) {
        if (s.listTitle) h += `<p class="lt">${esc(s.listTitle)}</p>`;
        h += `<ul>`; s.listItems.forEach((it) => { h += `<li>${esc(it)}</li>`; }); h += `</ul>`;
      }
      if (s.content) h += `<p>${esc(s.content)}</p>`;
      if (s.callout) h += `<blockquote>${s.callout.split('\n').map((l) => esc(l)).join('<br/>')}</blockquote>`;
      return h;
    }).join('');
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>${esc(d.title)}</title>
      <style>
        @media print { @page { margin: 2cm; } }
        body { font-family: Georgia, 'Times New Roman', serif; color:#2f2a24; line-height:1.6; max-width:720px; margin:32px auto; padding:0 20px; }
        h1 { font-size:26px; color:#3a4a3a; margin:0 0 4px; }
        .sub { color:#8a6a5a; font-style:italic; margin:0 0 20px; }
        h2 { font-size:19px; color:#3a4a3a; margin:26px 0 8px; border-bottom:1px solid #e5ded2; padding-bottom:4px; }
        p { margin:0 0 10px; }
        .group { margin:10px 0; }
        .gt { font-weight:bold; color:#5a4a3a; margin:12px 0 4px; }
        .lt { font-weight:bold; margin-top:14px; }
        ul { margin:4px 0 10px; padding-left:22px; }
        li { margin:3px 0; }
        blockquote { border-left:3px solid #8a6a5a; margin:14px 0; padding:8px 16px; font-style:italic; color:#3a4a3a; background:#f7f3ec; }
        .intro { font-size:16px; color:#4a4a4a; }
        .diagram { display:block; max-width:70%; margin:18px auto; }
        .cap { text-align:center; font-size:12px; color:#888; font-style:italic; margin:-8px 0 18px; }
        .foot { margin-top:28px; font-size:12px; color:#999; border-top:1px solid #e5ded2; padding-top:10px; }
      </style></head><body>
      <h1>${esc(d.title)}</h1>
      <p class="sub">${esc(d.subtitle || '')}</p>
      ${d.introduction ? `<p class="intro">${esc(d.introduction)}</p>` : ''}
      ${d.overviewImage ? `<img class="diagram" src="${esc(d.overviewImage)}" alt="overview"/>${d.overviewImageCaption ? `<p class="cap">${esc(d.overviewImageCaption)}</p>` : ''}` : ''}
      ${secHtml}
      <p class="foot">Grounded Wellness with Jill \u2014 for reflection and education. Not a substitute for medical or psychological care.</p>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  return (
    <div className="bg-cream pt-24 min-h-screen" data-testid="whole-self-resources-page">
      {/* Header */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/resources"
              className="flex items-center gap-2 text-stone-500 hover:text-forest font-body mb-6 transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Resources
            </Link>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
              <div>
                <h1 className="font-heading text-4xl md:text-5xl text-forest">
                  Whole Self Resources
                </h1>
                <p className="text-stone-500 font-body mt-2 max-w-xl">
                  Tools for the inner journey—meditation, emotional work, and connecting mind, body, and spirit.
                </p>
              </div>

              {/* Search */}
              <div className="relative w-full md:w-72">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-stone-300 rounded-full pl-11 pr-4 py-2.5 text-forest text-sm placeholder:text-stone-400 focus:outline-none focus:border-terracotta"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, index) => {
              const hasDetailedContent = !!getDetailedContent(resource.id);
              return (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => handleResourceClick(resource)}
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-lg transition-all duration-500 h-full flex flex-col">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={resource.image}
                        alt={resource.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 rounded-full font-body text-xs border ${getTypeColor(resource.type)}`}>
                          {resource.type}
                        </span>
                      </div>
                      {(hasDetailedContent || resource.downloadUrl) && (
                        <div className="absolute inset-0 bg-forest/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <span className="bg-white text-forest px-4 py-2 rounded-full font-body text-sm font-medium">
                            {hasDetailedContent ? 'View Resource' : 'Download Guide'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-heading text-xl text-forest mb-2 group-hover:text-terracotta transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-stone-500 font-body text-sm mb-4 flex-1">
                        {resource.description}
                      </p>

                      {/* Topics */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {resource.topics.slice(0, 3).map((topic, i) => (
                          <span key={i} className="px-2 py-1 bg-stone-100 text-stone-600 rounded-full text-xs font-body">
                            {topic}
                          </span>
                        ))}
                      </div>

                      {/* Action */}
                      <div className="flex items-center gap-2 text-terracotta font-body text-sm font-medium">
                        {hasDetailedContent ? (
                          <>
                            <BookOpen size={16} />
                            Read Resource
                            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                          </>
                        ) : resource.downloadUrl ? (
                          <>
                            <Download size={16} />
                            Download Guide
                          </>
                        ) : resource.type === 'Coming Soon' ? (
                          <span className="text-stone-400 italic">Coming soon...</span>
                        ) : (
                          <span className="text-stone-400">Content in development</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-16">
              <p className="text-stone-500 font-body">No resources found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* Resource Detail Modal */}
      <AnimatePresence>
        {selectedResource?.detailed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={handleCloseResource}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-cream rounded-2xl max-w-3xl w-full my-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative">
                <img
                  src={selectedResource.detailed.image}
                  alt={selectedResource.detailed.title}
                  className="w-full h-56 object-cover rounded-t-2xl"
                />
                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={() => copyResourceLink(selectedResource.id)}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    title="Copy shareable link"
                    data-testid="copy-resource-link"
                  >
                    {copied ? <Check size={20} className="text-emerald-600" /> : <Link2 size={20} className="text-forest" />}
                  </button>
                  <button
                    onClick={handleCloseResource}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    data-testid="close-resource-modal"
                  >
                    <X size={20} className="text-forest" />
                  </button>
                </div>
                {copied && (
                  <div className="absolute top-16 right-4 bg-emerald-600 text-white text-xs font-body px-3 py-1 rounded-full shadow-lg">
                    Link copied!
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-forest/80 to-transparent p-6">
                  <p className="text-cream/80 font-body text-sm mb-1">{selectedResource.detailed.subtitle}</p>
                  <h2 className="font-heading text-3xl text-white">
                    {selectedResource.detailed.title}
                  </h2>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 md:p-8 space-y-8">
                {/* Print / Save as PDF */}
                {selectedResource.detailed.sections && (
                  <div className="flex justify-end -mb-4">
                    <button
                      onClick={() => printArticle(selectedResource.detailed)}
                      className="inline-flex items-center gap-2 bg-forest text-cream font-body text-sm px-4 py-2 rounded-full hover:bg-forest-dark transition-colors"
                      data-testid="print-article-btn"
                    >
                      <Download size={15} /> Print / Save as PDF
                    </button>
                  </div>
                )}
                {/* Introduction */}
                {selectedResource.detailed.introduction && (
                  <div>
                    <p className="text-stone-600 font-body leading-relaxed">
                      {selectedResource.detailed.introduction}
                    </p>
                  </div>
                )}

                {/* Overview diagram */}
                {selectedResource.detailed.overviewImage && (
                  <figure className="my-2">
                    <img
                      src={selectedResource.detailed.overviewImage}
                      alt={`${selectedResource.detailed.title} overview`}
                      className="w-full max-w-md mx-auto rounded-xl shadow-md"
                      data-testid="article-overview-image"
                    />
                    {selectedResource.detailed.overviewImageCaption && (
                      <figcaption className="text-center text-stone-500 font-body text-xs italic mt-3">
                        {selectedResource.detailed.overviewImageCaption}
                      </figcaption>
                    )}
                  </figure>
                )}

                {/* Section-based articles (moved from Wellness) */}
                {selectedResource.detailed.sections && (
                  <ArticleSections detailed={selectedResource.detailed} />
                )}

                {/* Constriction vs Expansion (All Healing Is Self Healing) */}
                {selectedResource.detailed.constrictionExpansion && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.constrictionExpansion.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.constrictionExpansion.title}</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-stone-100 rounded-xl p-5">
                        <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.constrictionExpansion.constriction.title}</h4>
                        <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.constrictionExpansion.constriction.description}</p>
                      </div>
                      <div className="bg-terracotta/10 rounded-xl p-5">
                        <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.constrictionExpansion.expansion.title}</h4>
                        <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.constrictionExpansion.expansion.description}</p>
                      </div>
                    </div>
                    <p className="text-stone-600 font-body italic text-center">{selectedResource.detailed.constrictionExpansion.insight}</p>
                  </div>
                )}

                {/* What Creates Constriction */}
                {selectedResource.detailed.whatCreatesConstriction && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.whatCreatesConstriction.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.whatCreatesConstriction.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.whatCreatesConstriction.intro}</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.whatCreatesConstriction.signs.map((sign, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{sign}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-forest text-cream rounded-lg p-4 mb-4">
                      <p className="font-body font-medium mb-2">{selectedResource.detailed.whatCreatesConstriction.root}</p>
                      <p className="font-body text-sm text-cream/80">A subtle internal stance of:</p>
                      <ul className="mt-2 space-y-1">
                        {selectedResource.detailed.whatCreatesConstriction.stances.map((stance, idx) => (
                          <li key={idx} className="font-body text-sm italic">"{stance}"</li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.whatCreatesConstriction.impact}</p>
                  </div>
                )}

                {/* Expansion Section */}
                {selectedResource.detailed.expansionSection && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.expansionSection.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.expansionSection.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.expansionSection.intro}</p>
                    <p className="font-body text-stone-500 mb-2">Expansion shows up when:</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.expansionSection.showsUp.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">✓</span>{item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-forest font-heading italic">{selectedResource.detailed.expansionSection.key}</p>
                  </div>
                )}

                {/* Simplest Truth */}
                {selectedResource.detailed.simplestTruth && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.simplestTruth.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.simplestTruth.title}</h3>
                    </div>
                    <p className="font-body text-cream/90">{selectedResource.detailed.simplestTruth.content}</p>
                  </div>
                )}

                {/* Best Friend */}
                {selectedResource.detailed.bestFriend && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.bestFriend.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bestFriend.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.bestFriend.intro}</p>
                    <p className="font-body text-stone-500 mb-2">Non-abandonment means:</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.bestFriend.means.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-stone-600 font-body text-sm">
                          <span className="text-terracotta">•</span>{item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-forest font-heading italic">{selectedResource.detailed.bestFriend.key}</p>
                  </div>
                )}

                {/* Mindfulness */}
                {selectedResource.detailed.mindfulness && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.mindfulness.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.mindfulness.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.mindfulness.intro}</p>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.mindfulness.insight}</p>
                    <p className="font-body text-stone-500 mb-2">Mindfulness allows us to pause and ask:</p>
                    <ul className="space-y-2 mb-4">
                      {selectedResource.detailed.mindfulness.questions.map((q, idx) => (
                        <li key={idx} className="text-stone-600 font-body text-sm italic">"{q}"</li>
                      ))}
                    </ul>
                    <div className="bg-forest text-cream rounded-lg p-4">
                      <p className="font-body text-sm">{selectedResource.detailed.mindfulness.truth}</p>
                    </div>
                  </div>
                )}

                {/* The Shift */}
                {selectedResource.detailed.theShift && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.theShift.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.theShift.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-2">{selectedResource.detailed.theShift.intro}</p>
                    <p className="text-stone-500 font-body text-sm mb-4 italic">{selectedResource.detailed.theShift.note}</p>
                    <p className="font-body text-stone-500 mb-3">Expansion comes online when we move from:</p>
                    <div className="space-y-2 mb-4">
                      {selectedResource.detailed.theShift.moves.map((move, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-stone-600 font-body text-sm">
                          <span className="text-stone-400">{move.from}</span>
                          <span className="text-terracotta">→</span>
                          <span className="text-forest font-medium">{move.to}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-forest font-heading italic">{selectedResource.detailed.theShift.insight}</p>
                  </div>
                )}

                {/* Final Thought */}
                {selectedResource.detailed.finalThought && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.finalThought.icon} size={24} />
                      <h3 className="font-heading text-xl text-terracotta">{selectedResource.detailed.finalThought.title}</h3>
                    </div>
                    <p className="font-body text-cream/90 leading-relaxed">{selectedResource.detailed.finalThought.content}</p>
                  </div>
                )}

                {/* Key Insight (Creating Harmony) */}
                {selectedResource.detailed.keyInsight && (
                  <div className="bg-terracotta/10 border-l-4 border-terracotta p-5 rounded-r-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.keyInsight.icon} size={20} />
                      <h3 className="font-heading text-lg text-forest">{selectedResource.detailed.keyInsight.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.keyInsight.content}</p>
                  </div>
                )}

                {/* Reflection Question */}
                {selectedResource.detailed.reflectionQuestion && (
                  <div className="bg-forest text-cream rounded-xl p-6 text-center">
                    <p className="font-heading text-xl italic">"{selectedResource.detailed.reflectionQuestion}"</p>
                  </div>
                )}

                {/* Circle of Perspective */}
                {selectedResource.detailed.circleOfPerspective && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.circleOfPerspective.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.circleOfPerspective.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-4">{selectedResource.detailed.circleOfPerspective.intro}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {selectedResource.detailed.circleOfPerspective.areas.map((area, idx) => (
                        <div key={idx} className="text-center p-3 bg-stone-50 rounded-lg">
                          <span className="flex justify-center mb-2">
                            <SectionIcon icon={area.icon} size={32} />
                          </span>
                          <span className="text-stone-600 font-body text-xs">{area.name}</span>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-stone-500 font-body text-sm italic bg-stone-50 p-3 rounded-lg">
                      {selectedResource.detailed.circleOfPerspective.purpose}
                    </p>
                  </div>
                )}

                {/* Coaching Questions */}
                {selectedResource.detailed.coachingQuestions && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.coachingQuestions.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.coachingQuestions.title}</h3>
                    </div>
                    <div className="space-y-3">
                      {selectedResource.detailed.coachingQuestions.questions.map((q, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 shadow-sm flex gap-3">
                          <span className="w-7 h-7 bg-terracotta text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">{idx + 1}</span>
                          <p className="text-stone-600 font-body text-sm">{q}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Roving Priorities */}
                {selectedResource.detailed.rovingPriorities && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.rovingPriorities.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.rovingPriorities.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.rovingPriorities.intro}</p>
                    <p className="text-amber-700 bg-amber-50 px-4 py-2 rounded-lg font-body text-sm mb-4">
                      ⚠️ {selectedResource.detailed.rovingPriorities.insight}
                    </p>
                    
                    {selectedResource.detailed.rovingPriorities.poohReference && (
                      <div className="bg-stone-100 rounded-xl p-5 mb-4">
                        <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.rovingPriorities.poohReference.title}</h4>
                        <p className="text-stone-600 font-body text-sm italic">{selectedResource.detailed.rovingPriorities.poohReference.content}</p>
                      </div>
                    )}
                    
                    <div className="space-y-2 mb-4">
                      {selectedResource.detailed.rovingPriorities.categories.map((cat, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="font-body font-semibold text-forest">{cat.name}</span>
                          {cat.subcategories.length > 0 && (
                            <span className="text-stone-500 font-body text-sm"> — {cat.subcategories.join(', ')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <p className="text-emerald-700 font-body text-sm">📝 {selectedResource.detailed.rovingPriorities.exercise}</p>
                    </div>
                  </div>
                )}

                {/* Sources */}
                {selectedResource.detailed.sources && (
                  <div className="bg-stone-100 rounded-xl p-4">
                    <p className="font-body font-semibold text-forest text-sm mb-2">{selectedResource.detailed.sources.title}</p>
                    <ul className="space-y-1">
                      {selectedResource.detailed.sources.items.map((item, idx) => (
                        <li key={idx} className="text-stone-600 font-body text-sm">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Earthly Explanation (Empath Enhancement Guide) */}
                {selectedResource.detailed.earthlyExplanation && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.earthlyExplanation.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.earthlyExplanation.title}</h3>
                    </div>
                    
                    {/* Psychological Roots */}
                    <div className="mb-6">
                      <h4 className="font-heading text-lg text-terracotta mb-3">{selectedResource.detailed.earthlyExplanation.psychologicalRoots.title}</h4>
                      <div className="space-y-3">
                        {selectedResource.detailed.earthlyExplanation.psychologicalRoots.items.map((item, idx) => (
                          <div key={idx} className="bg-stone-50 rounded-lg p-4">
                            <p className="font-body font-semibold text-forest text-sm mb-1">{item.name}</p>
                            <p className="text-stone-600 font-body text-sm">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Physiological Aspects */}
                    <div>
                      <h4 className="font-heading text-lg text-terracotta mb-3">{selectedResource.detailed.earthlyExplanation.physiologicalAspects.title}</h4>
                      <div className="space-y-3">
                        {selectedResource.detailed.earthlyExplanation.physiologicalAspects.items.map((item, idx) => (
                          <div key={idx} className="bg-stone-50 rounded-lg p-4">
                            <p className="font-body font-semibold text-forest text-sm mb-1">{item.name}</p>
                            <p className="text-stone-600 font-body text-sm">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sacred Explanation (Empath Enhancement Guide) */}
                {selectedResource.detailed.sacredExplanation && (
                  <div className="bg-violet-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.sacredExplanation.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.sacredExplanation.title}</h3>
                    </div>
                    
                    {/* Energetic Sensitivity */}
                    <div className="mb-6">
                      <h4 className="font-heading text-lg text-violet-700 mb-2">{selectedResource.detailed.sacredExplanation.energeticSensitivity.title}</h4>
                      <p className="text-stone-600 font-body text-sm mb-3">{selectedResource.detailed.sacredExplanation.energeticSensitivity.intro}</p>
                      <div className="space-y-3">
                        {selectedResource.detailed.sacredExplanation.energeticSensitivity.items.map((item, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-4">
                            <p className="font-body font-semibold text-forest text-sm mb-1">{item.name}</p>
                            <p className="text-stone-600 font-body text-sm">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Chakra Connections */}
                    <div>
                      <h4 className="font-heading text-lg text-violet-700 mb-3">{selectedResource.detailed.sacredExplanation.chakraConnections.title}</h4>
                      <div className="space-y-3">
                        {selectedResource.detailed.sacredExplanation.chakraConnections.items.map((item, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-4">
                            <p className="font-body font-semibold text-forest text-sm mb-1">{item.name}</p>
                            <p className="text-stone-600 font-body text-sm">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Empath Experience (Empath Enhancement Guide) */}
                {selectedResource.detailed.empathExperience && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.empathExperience.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.empathExperience.title}</h3>
                    </div>
                    <div className="space-y-3">
                      {selectedResource.detailed.empathExperience.experiences.map((exp, idx) => (
                        <div key={idx} className="border-l-4 border-terracotta pl-4 py-2">
                          <p className="font-body font-semibold text-forest text-sm mb-1">{exp.name}</p>
                          <p className="text-stone-600 font-body text-sm">{exp.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strengths and Challenges (Empath Enhancement Guide) */}
                {selectedResource.detailed.strengthsAndChallenges && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-emerald-50 rounded-xl p-5">
                      <h4 className="font-heading text-lg text-emerald-700 mb-3">{selectedResource.detailed.strengthsAndChallenges.strengths.title}</h4>
                      <ul className="space-y-2">
                        {selectedResource.detailed.strengthsAndChallenges.strengths.items.map((item, idx) => (
                          <li key={idx} className="text-stone-600 font-body text-sm flex items-start gap-2">
                            <span className="text-emerald-500">✓</span> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-5">
                      <h4 className="font-heading text-lg text-amber-700 mb-3">{selectedResource.detailed.strengthsAndChallenges.challenges.title}</h4>
                      <ul className="space-y-2">
                        {selectedResource.detailed.strengthsAndChallenges.challenges.items.map((item, idx) => (
                          <li key={idx} className="text-stone-600 font-body text-sm flex items-start gap-2">
                            <span className="text-amber-500">!</span> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Protecting Energy (Empath Enhancement Guide) */}
                {selectedResource.detailed.protectingEnergy && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.protectingEnergy.icon} size={24} />
                      <h3 className="font-heading text-xl">{selectedResource.detailed.protectingEnergy.title}</h3>
                    </div>
                    <div className="space-y-6">
                      {selectedResource.detailed.protectingEnergy.sections.map((section, sIdx) => (
                        <div key={sIdx}>
                          <h4 className="font-heading text-lg text-terracotta mb-3">{section.title}</h4>
                          <div className="space-y-2">
                            {section.items.map((item, idx) => (
                              <div key={idx} className="bg-cream/10 rounded-lg p-3">
                                <p className="font-body font-semibold text-cream text-sm mb-1">{item.name}</p>
                                <p className="text-cream/80 font-body text-sm">{item.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tools for Thriving (Empath Enhancement Guide) */}
                {selectedResource.detailed.toolsForThriving && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.toolsForThriving.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.toolsForThriving.title}</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedResource.detailed.toolsForThriving.tools.map((tool, idx) => (
                        <div key={idx} className="bg-stone-50 rounded-lg p-4">
                          <h4 className="font-body font-semibold text-forest text-sm mb-2">{tool.title}</h4>
                          <ul className="space-y-1">
                            {tool.items.map((item, iIdx) => (
                              <li key={iIdx} className="text-stone-600 font-body text-xs">• {item}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Practical Exercises (Empath Enhancement Guide) */}
                {selectedResource.detailed.practicalExercises && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.practicalExercises.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.practicalExercises.title}</h3>
                    </div>
                    <div className="space-y-3">
                      {selectedResource.detailed.practicalExercises.exercises.map((ex, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 flex gap-3">
                          <span className="w-8 h-8 bg-terracotta text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">{idx + 1}</span>
                          <div>
                            <p className="font-body font-semibold text-forest text-sm">{ex.name}</p>
                            <p className="text-stone-600 font-body text-sm">{ex.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Future Aspects (Empath Enhancement Guide) */}
                {selectedResource.detailed.futureAspects && (
                  <div className="bg-stone-100 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.futureAspects.icon} size={20} />
                      <h3 className="font-heading text-lg text-forest">{selectedResource.detailed.futureAspects.title}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedResource.detailed.futureAspects.items.map((item, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-white text-stone-600 rounded-full text-xs font-body shadow-sm">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 6 Levels of Awareness */}
                {selectedResource.detailed.levelsOfAwareness && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.levelsOfAwareness.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.levelsOfAwareness.title}</h3>
                    </div>
                    <p className="text-stone-500 font-body text-sm mb-4">{selectedResource.detailed.levelsOfAwareness.subtitle}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedResource.detailed.levelsOfAwareness.levels.map((level, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-violet-50 px-3 py-2 rounded-lg">
                          <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-xs font-bold">{level.number}</span>
                          <span className="text-violet-700 font-body text-sm">{level.name}</span>
                        </div>
                      ))}
                    </div>
                    
                    {selectedResource.detailed.levelsOfAwareness.practice && (
                      <div className="bg-terracotta/10 rounded-lg p-4 mt-4">
                        <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.levelsOfAwareness.practice.title}</h4>
                        <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.levelsOfAwareness.practice.content}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* 7 Attitudinal Factors */}
                {selectedResource.detailed.attitudinalFactors && (
                  <div className="bg-emerald-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.attitudinalFactors.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.attitudinalFactors.title}</h3>
                    </div>
                    <p className="text-stone-500 font-body text-sm italic mb-4">{selectedResource.detailed.attitudinalFactors.note}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedResource.detailed.attitudinalFactors.factors.map((factor, idx) => (
                        <span key={idx} className="px-3 py-2 bg-white text-emerald-700 rounded-full text-sm font-body shadow-sm">
                          {idx + 1}. {factor}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preconditions */}
                {selectedResource.detailed.preconditions && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <SectionIcon icon={selectedResource.detailed.preconditions.icon} size={24} />
                      <h3 className="font-heading text-xl">{selectedResource.detailed.preconditions.title}</h3>
                    </div>
                    <div className="flex gap-4 mb-4">
                      {selectedResource.detailed.preconditions.items.map((item, idx) => (
                        <span key={idx} className="px-4 py-2 bg-cream/20 rounded-full font-body text-sm">{item}</span>
                      ))}
                    </div>
                    <p className="text-cream/80 font-body text-sm italic">{selectedResource.detailed.preconditions.insight}</p>
                  </div>
                )}

                {/* 5 Stages */}
                {selectedResource.detailed.fiveStages && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.fiveStages.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.fiveStages.title}</h3>
                    </div>
                    <p className="text-stone-500 font-body text-sm mb-4">{selectedResource.detailed.fiveStages.subtitle}</p>
                    <div className="flex flex-wrap gap-3">
                      {selectedResource.detailed.fiveStages.stages.map((stage, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-lg">
                          <span className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{stage.number}</span>
                          <span className="text-amber-800 font-body">{stage.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 7 Homecomings */}
                {selectedResource.detailed.sevenHomecomings && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.sevenHomecomings.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.sevenHomecomings.title}</h3>
                    </div>
                    <p className="text-stone-500 font-body text-sm mb-4">{selectedResource.detailed.sevenHomecomings.subtitle}</p>
                    <p className="text-stone-600 font-body text-sm mb-4">{selectedResource.detailed.sevenHomecomings.intro}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                      {selectedResource.detailed.sevenHomecomings.items.map((item, idx) => (
                        <div key={idx} className="text-center p-3 bg-stone-50 rounded-lg">
                          <span className="w-6 h-6 bg-terracotta text-white rounded-full inline-flex items-center justify-center text-xs font-bold mb-1">{idx + 1}</span>
                          <p className="text-stone-600 font-body text-xs">{item}</p>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-stone-500 font-body text-sm italic bg-stone-50 p-3 rounded-lg">{selectedResource.detailed.sevenHomecomings.closing}</p>
                  </div>
                )}

                {/* Meditation Types */}
                {selectedResource.detailed.meditationTypes && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.meditationTypes.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.meditationTypes.title}</h3>
                    </div>
                    
                    {/* Formal */}
                    <div className="bg-violet-50 rounded-xl p-5 mb-4">
                      <h4 className="font-heading text-lg text-violet-800 mb-3">{selectedResource.detailed.meditationTypes.formal.title}</h4>
                      <p className="text-stone-600 font-body text-sm mb-4">{selectedResource.detailed.meditationTypes.formal.content}</p>
                      
                      <p className="font-body font-semibold text-forest text-sm mb-2">Posture Points:</p>
                      <div className="grid md:grid-cols-2 gap-2 mb-4">
                        {selectedResource.detailed.meditationTypes.formal.posture.map((p, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3">
                            <span className="font-body font-semibold text-violet-700 text-sm">{p.point}:</span>
                            <span className="text-stone-600 font-body text-xs"> {p.instruction}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-body font-semibold text-forest text-sm mb-2">{selectedResource.detailed.meditationTypes.formal.breathExercise.title}</h5>
                        <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.meditationTypes.formal.breathExercise.instruction}</p>
                      </div>
                    </div>
                    
                    {/* Informal */}
                    <div className="bg-stone-100 rounded-xl p-5">
                      <h4 className="font-heading text-lg text-forest mb-3">{selectedResource.detailed.meditationTypes.informal.title}</h4>
                      <div className="space-y-3">
                        {selectedResource.detailed.meditationTypes.informal.types.map((type, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3">
                            <span className="font-body font-semibold text-forest text-sm">{type.name}</span>
                            <p className="text-stone-600 font-body text-xs mt-1">{type.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Quantum Psychology */}
                {selectedResource.detailed.quantumPsychology && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.quantumPsychology.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.quantumPsychology.title}</h3>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-stone-100 rounded-xl p-4">
                        <h4 className="font-body font-semibold text-stone-600 text-sm mb-2">{selectedResource.detailed.quantumPsychology.comparison.other.title}</h4>
                        <p className="text-stone-500 font-body text-xs">{selectedResource.detailed.quantumPsychology.comparison.other.purpose}</p>
                      </div>
                      <div className="bg-forest text-cream rounded-xl p-4">
                        <h4 className="font-body font-semibold text-sm mb-2">{selectedResource.detailed.quantumPsychology.comparison.qp.title}</h4>
                        <p className="text-cream/80 font-body text-xs">{selectedResource.detailed.quantumPsychology.comparison.qp.purpose}</p>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-5 shadow-sm mb-4">
                      <h4 className="font-heading text-lg text-forest mb-2">{selectedResource.detailed.quantumPsychology.oneSubstance.title}</h4>
                      <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.quantumPsychology.oneSubstance.content}</p>
                    </div>
                    
                    <div className="bg-terracotta text-white rounded-xl p-4 text-center">
                      <p className="font-heading text-xl">{selectedResource.detailed.quantumPsychology.keyInsight}</p>
                    </div>
                  </div>
                )}

                {/* Nervous System Coding */}
                {selectedResource.detailed.nervousSystemCoding && (
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.nervousSystemCoding.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.nervousSystemCoding.title}</h3>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {selectedResource.detailed.nervousSystemCoding.examples.map((ex, idx) => (
                        <p key={idx} className="text-stone-600 font-body text-sm">→ {ex}</p>
                      ))}
                    </div>
                    
                    <div className="bg-amber-50 rounded-lg p-4">
                      <h4 className="font-body font-semibold text-amber-800 text-sm mb-2">{selectedResource.detailed.nervousSystemCoding.keyPrinciple.title}</h4>
                      <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.nervousSystemCoding.keyPrinciple.content}</p>
                    </div>
                  </div>
                )}

                {/* The Suggestion */}
                {selectedResource.detailed.suggestion && (
                  <div className="bg-terracotta/10 border-l-4 border-terracotta p-5 rounded-r-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <SectionIcon icon={selectedResource.detailed.suggestion.icon} size={20} />
                      <h3 className="font-heading text-lg text-forest">{selectedResource.detailed.suggestion.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body mb-3">{selectedResource.detailed.suggestion.content}</p>
                    <p className="text-amber-700 bg-amber-50 px-3 py-2 rounded-lg text-sm font-body mb-3">
                      ⚠️ {selectedResource.detailed.suggestion.warning}
                    </p>
                    <p className="text-stone-500 font-body text-sm italic">{selectedResource.detailed.suggestion.guidance}</p>
                  </div>
                )}

                {/* 9 Principals */}
                {selectedResource.detailed.ninePrincipals && (
                  <div className="bg-forest text-cream rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.ninePrincipals.icon} size={24} />
                      <h3 className="font-heading text-xl">{selectedResource.detailed.ninePrincipals.title}</h3>
                    </div>
                    <div className="space-y-2">
                      {selectedResource.detailed.ninePrincipals.principals.map((p, idx) => (
                        <div key={idx} className="flex gap-3 items-start">
                          <span className="w-6 h-6 bg-cream text-forest rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</span>
                          <p className="text-cream/90 font-body text-sm">{p}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Books */}
                {selectedResource.detailed.books && (
                  <div>
                    <h3 className="font-heading text-xl text-forest mb-4 flex items-center gap-2">
                      <BookOpen className="text-terracotta" size={20} />
                      Recommended Reading
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {selectedResource.detailed.books.map((book, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 shadow-sm flex items-center gap-3">
                          <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center text-lg">
                            📚
                          </div>
                          <div>
                            <p className="font-body font-semibold text-forest text-sm">{book.title}</p>
                            {book.author && <p className="text-stone-500 font-body text-xs">{book.author}</p>}
                            {book.note && <p className="text-stone-400 font-body text-xs italic">{book.note}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Workshop Agenda (Self-Sabotage) */}
                {selectedResource.detailed.agenda && (
                  <div className="bg-stone-50 rounded-xl p-6">
                    <h3 className="font-heading text-xl text-forest mb-4">Workshop Topics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {selectedResource.detailed.agenda.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white p-3 rounded-lg shadow-sm">
                          <span className="w-6 h-6 bg-terracotta text-white rounded-full flex items-center justify-center text-xs font-bold">{String(idx + 1).padStart(2, '0')}</span>
                          <span className="text-stone-700 font-body text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reflection Questions (Self-Sabotage) */}
                {selectedResource.detailed.reflection && (
                  <div className="bg-terracotta/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <SectionIcon icon={selectedResource.detailed.reflection.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.reflection.title}</h3>
                    </div>
                    <div className="space-y-3">
                      {selectedResource.detailed.reflection.questions.map((question, idx) => (
                        <div key={idx} className="flex gap-3 items-start">
                          <span className="text-terracotta font-heading text-lg">?</span>
                          <p className="text-stone-700 font-body italic">{question}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Forward Momentum - Personal Story */}
                {selectedResource.detailed.personalStory && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.personalStory.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.personalStory.title}</h3>
                    </div>
                    {selectedResource.detailed.personalStory.paragraphs.map((p, idx) => (
                      <p key={idx} className="text-stone-600 font-body leading-relaxed">{p}</p>
                    ))}
                    {selectedResource.detailed.personalStory.insight && (
                      <div className="bg-terracotta/10 rounded-xl p-4">
                        <p className="text-terracotta font-body italic">{selectedResource.detailed.personalStory.insight}</p>
                      </div>
                    )}
                    <p className="text-stone-600 font-body leading-relaxed">{selectedResource.detailed.personalStory.closing}</p>
                  </div>
                )}

                {/* Before Momentum */}
                {selectedResource.detailed.beforeMomentum && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.beforeMomentum.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.beforeMomentum.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.beforeMomentum.intro}</p>
                    <ul className="space-y-2 ml-4">
                      {selectedResource.detailed.beforeMomentum.patterns.map((p, idx) => (
                        <li key={idx} className="text-stone-600 font-body text-sm flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0"></span>{p}
                        </li>
                      ))}
                    </ul>
                    <p className="text-stone-600 font-body italic">{selectedResource.detailed.beforeMomentum.story}</p>
                    <div className="space-y-3">
                      {selectedResource.detailed.beforeMomentum.meanings.map((m, idx) => (
                        <div key={idx} className="bg-forest/5 rounded-xl p-4">
                          <p className="font-body font-medium text-forest text-sm">{m.person} made it mean:</p>
                          <p className="text-stone-600 font-body italic">"{m.meaning}"</p>
                          <p className="text-stone-500 font-body text-sm mt-1">{m.result}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                      <p className="text-terracotta font-heading italic">{selectedResource.detailed.beforeMomentum.insight}</p>
                    </div>
                  </div>
                )}

                {/* Question Never Asked */}
                {selectedResource.detailed.questionNeverAsked && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.questionNeverAsked.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.questionNeverAsked.title}</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-stone-100 rounded-xl p-4">
                        <p className="font-body text-stone-400 text-xs uppercase mb-2">Not these:</p>
                        {selectedResource.detailed.questionNeverAsked.notThese.map((q, i) => (
                          <p key={i} className="text-stone-500 font-body text-sm line-through">{q}</p>
                        ))}
                      </div>
                      <div className="bg-forest/5 rounded-xl p-4">
                        <p className="font-body text-forest text-xs uppercase mb-2">But these:</p>
                        {selectedResource.detailed.questionNeverAsked.butThese.map((q, i) => (
                          <p key={i} className="text-forest font-body text-sm font-medium">{q}</p>
                        ))}
                      </div>
                    </div>
                    <p className="text-stone-600 font-body italic text-center">{selectedResource.detailed.questionNeverAsked.insight}</p>
                  </div>
                )}

                {/* Lead System */}
                {selectedResource.detailed.leadSystem && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.leadSystem.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.leadSystem.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.leadSystem.intro}</p>
                    <div className="bg-forest/5 rounded-xl p-4">
                      <p className="font-body text-forest text-sm font-medium mb-2">It quietly determines:</p>
                      {selectedResource.detailed.leadSystem.determines.map((d, i) => (
                        <p key={i} className="text-stone-600 font-body text-sm flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0"></span>{d}
                        </p>
                      ))}
                    </div>
                    {selectedResource.detailed.leadSystem.example && (
                      <div className="bg-white rounded-xl border border-stone-200 p-5">
                        <h4 className="font-heading text-forest mb-3">{selectedResource.detailed.leadSystem.example.title}</h4>
                        <p className="text-stone-600 font-body text-sm mb-3">{selectedResource.detailed.leadSystem.example.description}</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {selectedResource.detailed.leadSystem.example.paths.map((path, i) => (
                            <div key={i} className={`rounded-xl p-4 ${i === 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                              <p className="font-body font-medium text-sm mb-2">{path.label}:</p>
                              {path.items.map((item, j) => (
                                <p key={j} className="text-stone-600 font-body text-xs">• {item}</p>
                              ))}
                            </div>
                          ))}
                        </div>
                        <p className="text-terracotta font-heading italic text-center mt-3">{selectedResource.detailed.leadSystem.example.insight}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Certainty & Uncertainty */}
                {selectedResource.detailed.certaintyUncertainty && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.certaintyUncertainty.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.certaintyUncertainty.title}</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-forest/5 rounded-xl p-5">
                        <h4 className="font-heading text-forest mb-2">Certainty</h4>
                        <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.certaintyUncertainty.certainty.intro}</p>
                        <p className="text-stone-500 font-body text-xs italic mt-2">{selectedResource.detailed.certaintyUncertainty.certainty.thought}</p>
                      </div>
                      <div className="bg-terracotta/10 rounded-xl p-5">
                        <h4 className="font-heading text-terracotta mb-2">Uncertainty</h4>
                        <p className="text-stone-600 font-body text-sm">{selectedResource.detailed.certaintyUncertainty.uncertainty.intro}</p>
                        {selectedResource.detailed.certaintyUncertainty.uncertainty.needs.map((n, i) => (
                          <p key={i} className="text-stone-600 font-body text-xs mt-1">• {n}</p>
                        ))}
                      </div>
                    </div>
                    <div className="bg-stone-100 rounded-xl p-4 text-center">
                      <p className="text-stone-600 font-body italic text-sm">{selectedResource.detailed.certaintyUncertainty.uncertainty.insight}</p>
                    </div>
                  </div>
                )}

                {/* Meeting Needs */}
                {selectedResource.detailed.meetingNeeds && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.meetingNeeds.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.meetingNeeds.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.meetingNeeds.intro}</p>
                    {selectedResource.detailed.meetingNeeds.examples.map((ex, i) => (
                      <div key={i} className="bg-white rounded-xl border border-stone-200 p-4">
                        <p className="font-heading text-forest mb-2">{ex.need}</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-emerald-600 font-body uppercase mb-1">Healthy</p>
                            {ex.healthy.map((h, j) => <p key={j} className="text-stone-600 font-body text-sm">• {h}</p>)}
                          </div>
                          <div>
                            <p className="text-xs text-red-500 font-body uppercase mb-1">Unhealthy</p>
                            {ex.unhealthy.map((u, j) => <p key={j} className="text-stone-600 font-body text-sm">• {u}</p>)}
                          </div>
                        </div>
                      </div>
                    ))}
                    <p className="text-terracotta font-heading italic text-center">{selectedResource.detailed.meetingNeeds.insight}</p>
                  </div>
                )}

                {/* Four vs Two */}
                {selectedResource.detailed.fourVsTwo && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.fourVsTwo.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.fourVsTwo.title}</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-forest/5 rounded-xl p-5">
                        <h4 className="font-heading text-forest mb-3">{selectedResource.detailed.fourVsTwo.personalityNeeds.title}</h4>
                        {selectedResource.detailed.fourVsTwo.personalityNeeds.needs.map((n, i) => (
                          <p key={i} className="text-stone-600 font-body text-sm mb-1">• {n}</p>
                        ))}
                        <p className="text-stone-500 font-body text-xs italic mt-3">"{selectedResource.detailed.fourVsTwo.personalityNeeds.quote}"</p>
                      </div>
                      <div className="bg-terracotta/10 rounded-xl p-5">
                        <h4 className="font-heading text-terracotta mb-3">{selectedResource.detailed.fourVsTwo.spiritualNeeds.title}</h4>
                        {selectedResource.detailed.fourVsTwo.spiritualNeeds.needs.map((n, i) => (
                          <p key={i} className="text-stone-600 font-body text-sm mb-1">• {n}</p>
                        ))}
                        <p className="text-stone-600 font-body text-sm mt-2">{selectedResource.detailed.fourVsTwo.spiritualNeeds.description}</p>
                        <p className="text-stone-500 font-body text-xs italic mt-2">{selectedResource.detailed.fourVsTwo.spiritualNeeds.warning}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Growth Feels Hard */}
                {selectedResource.detailed.growthFeelsHard && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.growthFeelsHard.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.growthFeelsHard.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body leading-relaxed">{selectedResource.detailed.growthFeelsHard.content}</p>
                  </div>
                )}

                {/* Meaning → Movement */}
                {selectedResource.detailed.meaningMovement && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.meaningMovement.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.meaningMovement.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body leading-relaxed">{selectedResource.detailed.meaningMovement.content}</p>
                  </div>
                )}

                {/* Forward Momentum */}
                {selectedResource.detailed.forwardMomentum && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.forwardMomentum.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.forwardMomentum.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.forwardMomentum.intro}</p>
                    <div className="space-y-3">
                      {selectedResource.detailed.forwardMomentum.alignments.map((a, i) => (
                        <div key={i} className="bg-forest/5 rounded-xl p-4">
                          <p className="font-body font-medium text-forest">{i + 1}. {a.name}</p>
                          <p className="text-stone-500 font-body text-sm italic">{a.question}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-stone-100 rounded-xl p-4">
                      {selectedResource.detailed.forwardMomentum.checks.map((c, i) => (
                        <p key={i} className="text-stone-600 font-body text-sm">• {c}</p>
                      ))}
                    </div>
                    <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                      <p className="text-terracotta font-heading italic">{selectedResource.detailed.forwardMomentum.truth}</p>
                    </div>
                  </div>
                )}

                {/* Quiet Reframe */}
                {selectedResource.detailed.quietReframe && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.quietReframe.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.quietReframe.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.quietReframe.intro}</p>
                    <div className="space-y-2">
                      {selectedResource.detailed.quietReframe.questions.map((q, i) => (
                        <div key={i} className="bg-forest/5 rounded-xl p-3 flex items-center gap-3">
                          <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{i + 1}</span>
                          <p className="text-stone-600 font-body text-sm">{q}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Body Never Lies */}
                {selectedResource.detailed.bodyNeverLies && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.bodyNeverLies.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bodyNeverLies.title}</h3>
                    </div>
                    {selectedResource.detailed.bodyNeverLies.paragraphs.map((p, idx) => (
                      <p key={idx} className="text-stone-600 font-body leading-relaxed">{p}</p>
                    ))}
                    <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                      <p className="text-terracotta font-heading italic">{selectedResource.detailed.bodyNeverLies.insight}</p>
                    </div>
                  </div>
                )}

                {/* Body Signals */}
                {selectedResource.detailed.bodySignals && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.bodySignals.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bodySignals.title}</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {selectedResource.detailed.bodySignals.signals.map((s, idx) => (
                        <div key={idx} className="bg-forest/5 rounded-xl p-3">
                          <p className="font-body text-forest text-sm font-medium">{s.signal}</p>
                          <p className="text-stone-500 font-body text-xs">{s.meaning}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-stone-600 font-body italic text-center">{selectedResource.detailed.bodySignals.insight}</p>
                  </div>
                )}

                {/* Symptoms as Messages */}
                {selectedResource.detailed.symptomsAsMessages && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.symptomsAsMessages.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.symptomsAsMessages.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body leading-relaxed">{selectedResource.detailed.symptomsAsMessages.content}</p>
                    <div className="space-y-2">
                      {selectedResource.detailed.symptomsAsMessages.questions.map((q, i) => (
                        <div key={i} className="bg-forest/5 rounded-xl p-3 flex items-center gap-3">
                          <span className="bg-terracotta text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading flex-shrink-0">{i+1}</span>
                          <p className="text-forest font-body text-sm italic">{q}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-stone-600 font-body leading-relaxed">{selectedResource.detailed.symptomsAsMessages.note}</p>
                    <div className="bg-terracotta/10 rounded-xl p-4 text-center">
                      <p className="text-terracotta font-heading italic">{selectedResource.detailed.symptomsAsMessages.insight}</p>
                    </div>
                  </div>
                )}

                {/* Body Map */}
                {selectedResource.detailed.bodyMap && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <SectionIcon icon={selectedResource.detailed.bodyMap.icon} size={24} />
                      <h3 className="font-heading text-xl text-forest">{selectedResource.detailed.bodyMap.title}</h3>
                    </div>
                    <p className="text-stone-600 font-body">{selectedResource.detailed.bodyMap.intro}</p>
                    <div className="grid gap-2">
                      {selectedResource.detailed.bodyMap.areas.map((b, i) => (
                        <div key={i} className="flex items-start gap-3 bg-forest/5 rounded-xl p-3">
                          <span className="bg-forest text-cream text-xs font-heading px-2 py-1 rounded-full flex-shrink-0 min-w-[90px] text-center">{b.area}</span>
                          <p className="text-stone-600 font-body text-sm">{b.meaning}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Closing Thought */}
                {selectedResource.detailed.closingThought && (
                  <div className="bg-stone-100 rounded-xl p-6 text-center">
                    <Heart className="text-terracotta mx-auto mb-3" size={24} />
                    <p className="text-forest font-body">
                      {selectedResource.detailed.closingThought}
                    </p>
                  </div>
                )}

                {/* Download Option */}
                {selectedResource.downloadUrl && (
                  <div className="pt-4 border-t border-stone-200">
                    <a
                      href={selectedResource.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-all"
                    >
                      <Download size={18} />
                      {selectedResource.type === 'Presentation' ? 'Download Presentation' : 'Download Original PDF'}
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Newsletter Soft Prompt */}
      <AnimatePresence>
        {showPrompt && (
          <NewsletterSignup 
            variant="prompt" 
            onClose={dismissPrompt} 
            source="whole-self-resources" 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default WholeSelfResourcesPage;
